import { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { FeaturedSvg } from "@/components/seo/FeaturedSvg";
import { ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

export interface InfoBreadcrumb { label: string; href?: string }

interface Props {
  variant: "about" | "contact" | "legal";
  title: string;
  subtitle?: string;
  badge?: string;
  lastUpdated?: string;
  breadcrumbs?: InfoBreadcrumb[];
  /** Optional sidebar (TOC, quick links). */
  aside?: ReactNode;
  /** Optional footer CTA below the article. */
  footer?: ReactNode;
  locale?: "id" | "en";
  children: ReactNode;
  className?: string;
}

export function InfoPageLayout({
  variant, title, subtitle, badge, lastUpdated,
  breadcrumbs, aside, footer, locale = "id", children, className,
}: Props) {
  return (
    <div className={cn("py-6 md:py-10", className)}>
      {/* Breadcrumbs */}
      {breadcrumbs && breadcrumbs.length > 0 ? (
        <nav aria-label="Breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <ol className="flex flex-wrap items-center gap-1">
            {breadcrumbs.map((b, i) => (
              <li key={i} className="flex items-center gap-1">
                {b.href ? (
                  <Link to={b.href as never} className="hover:text-foreground">{b.label}</Link>
                ) : (
                  <span className="text-foreground">{b.label}</span>
                )}
                {i < breadcrumbs.length - 1 ? <ChevronRight className="h-3.5 w-3.5 opacity-60" /> : null}
              </li>
            ))}
          </ol>
        </nav>
      ) : null}

      {/* Hero */}
      <header className="rounded-2xl border border-border bg-card p-5 md:p-7">
        <div className="flex flex-col items-start gap-5 md:flex-row md:items-center">
          <FeaturedSvg
            variant={variant}
            title={title}
            subtitle={subtitle}
            badge={badge}
            locale={locale}
            className="w-full max-w-xs md:w-72 md:flex-shrink-0"
          />
          <div className="flex-1">
            <h1 className="text-2xl font-bold tracking-tight md:text-3xl">{title}</h1>
            {subtitle ? (
              <p className="mt-2 text-sm text-muted-foreground md:text-base">{subtitle}</p>
            ) : null}
            {lastUpdated ? (
              <p className="mt-3 inline-flex rounded-full bg-muted px-3 py-1 text-xs text-muted-foreground">
                {locale === "en" ? "Last updated" : "Terakhir diperbarui"}: {lastUpdated}
              </p>
            ) : null}
          </div>
        </div>
      </header>

      {/* Body */}
      <div className="mt-8 grid gap-8 lg:grid-cols-[minmax(0,1fr)_240px]">
        <article className="prose prose-neutral max-w-none dark:prose-invert prose-headings:scroll-mt-24 prose-h2:text-xl prose-h2:font-semibold prose-h2:mt-8 prose-h2:mb-3 prose-p:leading-relaxed">
          {children}
        </article>
        {aside ? (
          <aside className="lg:sticky lg:top-20 lg:self-start">
            {aside}
          </aside>
        ) : null}
      </div>

      {footer ? <div className="mt-10">{footer}</div> : null}
    </div>
  );
}
